import "../../assets/less/tour/tour.less";
import "../../assets/css/app.css";
import "../../assets/css/tour/tour-upload.css";
import React, { useMemo, useEffect } from "react";
import { BraintreeProvider } from "../../braintree/BraintreeProvider";
import MainStateContext from "../../state/mainStateContext";
import { determineInitialState } from "../../state/mainInitialState";
import reducer from "../../state/mainStateReducer";
import Checkout from "../Shared/Checkout";
import StepContainer from "../Shared/StepContainer";
import AppOverlay from "../App/AppOverlay";
import {
  checkIsThankyouPage,
  getStoredChildData,
  removeStoredChildData,
  wvusLogger,
  triggerEventOnce,
  getQueryParam
} from "../../helpers";
import { storeSakSponsorData } from "../../helpers/client-state/sakLocalStorage";
import TourOrderConfirmation from "./TourOrderConfirmation";
import appSettings from "../../settings/appSettings";
import { useTokenConfig } from "../../hooks/useTokenConfig.js";
import TourChildProfile from "./TourChildProfile";
import { RCDSProvider } from "../../rcds/RCDSProvider.js";
import { OrderRequestProvider } from "../../orderRequest/OrderRequestProvider.js";
import { CheckoutData, LineItem, Product, TourAppProps } from "../../types";
import { PhraseKey } from "../../languages";
const randomChildLookupKeys = ["", "lookup", "random", "find", "search"];

export default function TourApp(props: TourAppProps) {
  const { opts } = props;
  const { checkoutFlow, allowMultipleQuantity } = appSettings;
  const initialState = determineInitialState();
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const hideStepTitles = true;
  const stateContextMemo = useMemo(() => {
    const findProductById = (productId: string): Product => {
      return state.products.find(
        (product: Product) => product.productId === productId || product.product_id === productId
      );
    };

    return {
      state,
      dispatch,
      setSubmitMessagePhraseKey: (phrase: PhraseKey) => {
        dispatch({ type: "SET_SUBMIT_MESSAGE_PHRASE_KEY", payload: phrase });
      },
      toggleOverlay: (overlayActive = state.appOverlayActive) => {
        dispatch({ type: "TOGGLE_APP_OVERLAY", payload: overlayActive });
      },
      findProductById,
      getProductQuantity: (productId: string): number | undefined => {
        const selectedProduct = findProductById(productId);
        if (!selectedProduct || (!selectedProduct.qty && !selectedProduct.quantity)) {
          return 0;
        } else {
          return selectedProduct.quantity || selectedProduct.qty;
        }
      }
    };
  }, [state, dispatch]);

  const initialRCDSState = useMemo(() => {
    const childIdParam = getQueryParam("childid");
    const isRandomChildLookup = !childIdParam || randomChildLookupKeys.includes(childIdParam);
    const childData = getStoredChildData();

    if (childData && isRandomChildLookup) {
      return {
        childData,
        status: "success"
      };
    }

    return {};
  }, [opts]);

  useEffect(() => {
    // Removes Pre-App Loader Graphic After Checkout Ready
    const isThankYouPage = checkIsThankyouPage();

    let timeoutId = null;

    const handler = function () {
      clearTimeout(timeoutId);

      // Remove Pre-App Loader
      const preAppLoader = document.querySelector(".wvus-streamlined-loader");
      if (preAppLoader) {
        preAppLoader.remove();
      }

      // Show disclaimer
      const disclaimer = document.querySelector(".disclaimer__text");
      if (disclaimer) {
        disclaimer.setAttribute("style", "opacity: 1");
      }
    };

    if (isThankYouPage) {
      handler();
      return;
    }

    window.addEventListener("app:checkout:ready", handler);

    timeoutId = setTimeout(() => {
      // Run This Timeout Just In Case The Event Doesn't Fire
      window.removeEventListener("app:checkout:ready", handler);
      handler();
    }, 3000);

    return () => {
      window.removeEventListener("app:checkout:ready", handler);
    };
  }, []);

  const { step1, step2, thankYouStep } = state;

  const tokenExConfigQuery = useTokenConfig();

  const addProductsToState = (payload: Product[]) => {
    // Validate Product
    dispatch({ type: "REPLACE_PRODUCT", payload: payload });
    dispatch({ type: "SET_STEP2", payload: { enabled: true } });
    triggerEventOnce("view_item", { products: payload });
  };

  const addProductsListener = (event: CustomEvent) => {
    if (event?.detail?.products?.length) {
      // TODO: Add Products Validation
      const payload = event.detail.products;

      addProductsToState(payload);
    } else {
      const error = new Error("No products found.");
      console.error(error);
      wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
    }
  };

  useEffect(() => {
    window.addEventListener("wvcheckout:addProducts", addProductsListener);

    return () => {
      window.removeEventListener("wvcheckout:addProducts", addProductsListener);
    };
  }, [state.products, initialRCDSState]);

  const onCheckoutLineItemsMap = (
    lineItems: LineItem[],
    checkoutData: CheckoutData
  ): LineItem[] => {
    const { billingAddress, timestamp } = checkoutData;
    const updatedLineItems = lineItems.map((lineItem: LineItem) => {
      return {
        ...lineItem,
        lineMeta: {
          ...(lineItem.lineMeta || {}),
          wvus_product_streamlined_failover: state?.rcdsFailover ? "true" : "false",
          wvus_product_goal_related_data: JSON.stringify({
            ...((lineItem?.lineMeta?.wvus_product_goal_related_data as object) || {}),
            timestamp: timestamp,
            donorData: {
              firstName: billingAddress?.firstName,
              lastName: billingAddress?.lastName
            }
          })
        }
      };
    });

    return updatedLineItems;
  };

  const checkoutFlowClass =
    !allowMultipleQuantity && checkoutFlow === "PaymentFirst" ? checkoutFlow : "";

  return (
    <MainStateContext.Provider value={stateContextMemo}>
      <OrderRequestProvider>
        <BraintreeProvider>
          <RCDSProvider initialState={initialRCDSState}>
            <AppOverlay />
            <div className={`App TourApp ${checkoutFlowClass}`}>
              <StepContainer {...step1} hideTitle={hideStepTitles}>
                <p className="tour__sponsor-child-title">Sponsor a Child with World Vision</p>
                <TourChildProfile
                  randomChildLookupKeys={randomChildLookupKeys}
                  addProductsToState={addProductsToState}
                />
              </StepContainer>

              <StepContainer {...step2} hideTitle={hideStepTitles}>
                <Checkout
                  tokenExConfigQuery={tokenExConfigQuery}
                  onCheckoutLineItemsMap={onCheckoutLineItemsMap}
                  onCheckoutSuccess={(data) => {
                    // Store billing address for SAK questionnaire
                    storeSakSponsorData(data?.billingAddress);
                    removeStoredChildData();
                  }}
                  formWrapperClassName="checkout__wrapper"
                  opts={opts}
                  ctaPhraseKey="tours.submission.cta"
                  designatedProductId="380"
                />
              </StepContainer>

              <StepContainer {...thankYouStep}>
                <TourOrderConfirmation />
              </StepContainer>
            </div>
          </RCDSProvider>
        </BraintreeProvider>
      </OrderRequestProvider>
    </MainStateContext.Provider>
  );
}
